import React, { useState, useEffect, useRef, useCallback } from "react";
import Icon from "@mdi/react";
import {
  mdiContentCopy,
  mdiCheckCircle,
  mdiPencil,
  mdiOpenInNew,
  mdiArrowLeft
} from "@mdi/js";
import axios from "axios";
import "./KartuAkun.css";

const REACT_APP_EXPRESS_API = process.env.REACT_APP_EXPRESS_API;

const API_BASE = `${REACT_APP_EXPRESS_API}`;

const KartuAkun = () => {
  const [user, setUser] = useState({
    id: null,
    telegramId: null,
  });
  const [loading, setLoading] = useState(true);

  // === Telegram Verification State ===
  const [isVerifying, setIsVerifying] = useState(false);
  const [telegramData, setTelegramData] = useState({
    verificationCode: "",
    telegramId: null,
    telegramUsername: null,
    verified: false,
  });
  const [message, setMessage] = useState("");
  const [polling, setPolling] = useState(false);
  const [linking, setLinking] = useState(false);

  const pollingInterval = useRef(null);
  const pollingLocked = useRef(false);

  const generateVerificationCode = useCallback(() => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }, []);

  const stopPolling = useCallback(() => {
    setPolling(false);
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    pollingLocked.current = false;
  }, []);

  const checkVerificationStatusOnce = useCallback(async () => {
    const code = telegramData.verificationCode;
    if (!code) return;
    try {
      const res = await axios.get(
        `${API_BASE}/api/telegram/check-verification/${code}`
      );
      if (res?.data?.verified) {
        setTelegramData((prev) => ({
          ...prev,
          telegramId: res.data.telegramId,
          telegramUsername: res.data.username,
          verified: true,
        }));
        setMessage("✓ Telegram berhasil diverifikasi!");
        stopPolling();
      }
    } catch { }
  }, [telegramData.verificationCode, stopPolling]);

  useEffect(() => {
    if (polling) {
      if (pollingLocked.current) return;
      pollingLocked.current = true;
      checkVerificationStatusOnce();
      pollingInterval.current = setInterval(() => {
        checkVerificationStatusOnce();
      }, 3000);
    } else {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
      pollingLocked.current = false;
    }
    return () => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
      pollingLocked.current = false;
    };
  }, [polling, checkVerificationStatusOnce]);

  useEffect(() => {
    if (isVerifying) {
      setTelegramData((prev) => {
        if (prev.verified || prev.verificationCode) return prev;
        return { ...prev, verificationCode: generateVerificationCode() };
      });
    }
  }, [isVerifying, generateVerificationCode]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(`/verify ${telegramData.verificationCode}`);
      setMessage("Kode berhasil disalin!");
      setTimeout(() => setMessage(""), 2000);
    } catch {
      setMessage("Gagal menyalin kode.");
    }
  };

  const handleStartVerification = () => {
    setMessage("");
    if (!telegramData.verificationCode)
      return setMessage("Kode verifikasi belum tersedia.");
    setPolling(true);
  };

  // UPDATE URL: Nanti Anda dapat mengubah URL backend di sini jika perlu.
  const handleLinkTelegram = async () => {
    if (!telegramData.verified || !user.id) return;
    setLinking(true);
    setMessage("");
    try {
      await axios.put(`${API_BASE}/api/users/update/${user.id}`, {
        telegramId: telegramData.telegramId,
      });
      setUser((prev) => ({ ...prev, telegramId: telegramData.telegramId }));
      setMessage("Akun Telegram berhasil dihubungkan!");
      setTimeout(() => {
        setIsVerifying(false);
        stopPolling();
        setTelegramData({
          verificationCode: "",
          telegramId: null,
          telegramUsername: null,
          verified: false,
        });
        setMessage("");
      }, 1500);
    } catch (err) {
      setMessage(
        err.response?.data?.message || "Gagal menghubungkan Telegram. Coba lagi nanti."
      );
    } finally {
      setLinking(false);
    }
  };

  const handleCancel = () => {
    setIsVerifying(false);
    stopPolling();
    setMessage("");
    setTelegramData({
      verificationCode: "",
      telegramId: null,
      telegramUsername: null,
      verified: false,
    });
  };

  useEffect(() => {
    const ac = new AbortController();

    const fetchUser = async () => {
      try {
        const userRes = await fetch(`${API_BASE}/api/users/current`, {
          credentials: "include",
          signal: ac.signal,
        });
        const userData = await userRes.json();

        if (userData?.user) {
          setUser({
            id: userData.user._id || userData.user.id,
            telegramId: userData.user.telegramId || null,
          });
        }
      } catch (err) {
        console.error("Gagal memuat data pengguna:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
    return () => ac.abort();
  }, []);

  if (loading) {
    return (
      <div className="telegram-card loading">
        <p style={{ textAlign: "center", padding: "30px", color: "#666" }}>
          Memuat data...
        </p>
      </div>
    );
  }

  return (
    <div className="telegram-card fade-in">
      {!isVerifying ? (
        <div className="telegram-card-main">
          <h2>Koneksi Telegram</h2>
          {user.telegramId ? (
            <div className="status-connected">
              <div className="status-badge connected">
                <Icon path={mdiCheckCircle} size={0.8} /> Terhubung
              </div>
              <p className="telegram-id-display">ID Telegram Anda: <strong>{user.telegramId}</strong></p>

              <div className="action-buttons">
                <a
                  href="https://web.telegram.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-telegram-web"
                >
                  <Icon path={mdiOpenInNew} size={0.8} /> Buka Web Telegram
                </a>
                <button
                  className="btn-edit-telegram"
                  onClick={() => setIsVerifying(true)}
                >
                  <Icon path={mdiPencil} size={0.8} /> Edit Koneksi
                </button>
              </div>
            </div>
          ) : (
            <div className="status-disconnected">
              <div className="status-badge disconnected">
                Belum Terhubung
              </div>
              <p className="description">
                Hubungkan akun Telegram untuk mendapatkan notifikasi dan melakukan pencatatan transaksi dengan mudah.
              </p>
              <button
                className="btn-connect-telegram"
                onClick={() => setIsVerifying(true)}
              >
                Koneksikan ke Telegram
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="telegram-card-verify">
          <button className="back-btn" onClick={handleCancel}>
            <Icon path={mdiArrowLeft} size={1} /> Kembali
          </button>

          <div className="verify-header">
            <h3>Verifikasi Telegram</h3>
            <p className="subtitle">
              Ikuti langkah-langkah berikut untuk menghubungkan akun Anda
            </p>
          </div>

          <div className="verification-step">
            <ol className="verify-steps">
              <li>
                Buka <strong>Telegram</strong> dan cari{" "}
                <strong>
                  <a
                    href="https://t.me/lacakas_bot"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    @LacaKasBot
                  </a>
                </strong>
              </li>
              <li>Ketik <strong>/start</strong> untuk memulai</li>
              <li>Salin kode di bawah dan kirim ke bot</li>
            </ol>

            <div className="verification-code-box">
              <label>Kode Verifikasi Anda:</label>
              <div className="code-display">
                <span>/verify {telegramData.verificationCode || "—"}</span>
                <button
                  type="button"
                  className="copy-btn"
                  onClick={handleCopyCode}
                  disabled={!telegramData.verificationCode}
                  title="Salin Kode"
                >
                  <Icon path={mdiContentCopy} size={0.8} />
                </button>
              </div>
            </div>

            {!polling && !telegramData.verified && (
              <button
                type="button"
                className="start-verify-btn"
                onClick={handleStartVerification}
              >
                Mulai Verifikasi
              </button>
            )}

            {polling && !telegramData.verified && (
              <div className="polling-status">
                <div className="spinner" />
                <p>Menunggu verifikasi dari Telegram...</p>
              </div>
            )}

            {telegramData.verified && (
              <div className="verification-success">
                <Icon path={mdiCheckCircle} size={1} color="#4caf50" />
                <p>Telegram berhasil diverifikasi!</p>
              </div>
            )}

            {message && <p className={`status-message ${message.includes('✓') || message.includes('berhasil') ? 'success' : ''}`}>{message}</p>}

            <button
              className="submit-verify-btn"
              onClick={handleLinkTelegram}
              disabled={!telegramData.verified || linking}
            >
              {linking ? "Menyimpan..." : "Simpan Koneksi"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KartuAkun;

